import React, { useState, useEffect } from 'react';
import { PrepAdminLayout } from '../../../components/prep/PrepAdminLayout';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = { admin: 'bg-red-100 text-red-700', instructor: 'bg-purple-100 text-purple-700', student: 'bg-blue-100 text-blue-700' };

export const PrepAdminStudents = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = () => {
    axios.get(`${API}/prep/admin/users`, { withCredentials: true })
      .then(r => setUsers(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/prep/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success('Role updated');
      load();
    } catch { toast.error('Failed to update role'); }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <PrepAdminLayout title="Manage Students">
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full max-w-md px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="student-search"
        />
      </div>

      {loading ? <div className="text-center py-8 text-gray-500">Loading...</div> : (
        <div className="space-y-3">
          {filtered.map((u) => (
            <Card key={u.user_id} className="border">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {u.picture ? (
                      <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                        {u.name?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-gray-900">{u.name}</div>
                      <div className="text-sm text-gray-500">{u.email}</div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        Enrolled in {u.enrollment_count} program{u.enrollment_count !== 1 ? 's' : ''}
                        {u.created_at && ` · Joined ${new Date(u.created_at).toLocaleDateString()}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-700'}>{u.role}</Badge>
                    <select
                      value={u.role}
                      onChange={e => updateRole(u.user_id, e.target.value)}
                      className="px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid={`role-select-${u.user_id}`}
                    >
                      <option value="student">Student</option>
                      <option value="instructor">Instructor</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && <p className="text-center text-gray-500 py-8">No students found.</p>}
        </div>
      )}
    </PrepAdminLayout>
  );
};
